type UriResult = {
    protocol?: string | null;
    hostname?: string | null;
    host?: string | null;
    origin?: string | null;
    pathname?: string | null;
    port?: string | null;
    search?: string | null;
    hash?: string | null;
    href?: string | null;
    requestUri?: string | null;
};

export function parseUri(url: string): UriResult {
    const keys: (keyof UriResult)[] = 'protocol hostname host origin pathname port search hash href'.split(
        ' ',
    ) as (keyof UriResult)[];
    const result: UriResult = {} as UriResult;
    const anchor: HTMLAnchorElement = document.createElement('a');

    if (!url) {
        return result;
    }

    anchor.href = url;

    keys.forEach((key) => {
        result[key] = anchor[key as keyof HTMLAnchorElement] as string | null;
    });

    result.toString = function toString(): string {
        return anchor.href;
    };

    result.requestUri = (result.pathname ?? '') + (result.search ?? '');

    return result;
}

export function isObjectObject(o: GenericObjectType): boolean {
    return Object.prototype.toString.call(o) === '[object Object]';
}

export function isPlainObject(obj?: null | Record<string, unknown>): boolean {
    if (isObjectObject(obj) === false) {
        return false;
    }

    // If has modified constructor
    // eslint-disable-next-line @typescript-eslint/ban-types
    const ctor: Function | undefined = obj?.constructor;
    if (typeof ctor !== 'function') {
        return false;
    }

    // If has modified prototype
    const prot: Record<string, unknown> = ctor.prototype;
    if (isObjectObject(prot) === false) {
        return false;
    }

    // If constructor does not have an Object-specific method
    if (Object.prototype.hasOwnProperty.call(prot, 'isPrototypeOf') === false) {
        return false;
    }

    // Most likely a plain Object
    return true;
}

export function trimObject(obj: GenericObjectType, deep = false): Record<string, unknown> {
    const result: Record<string, unknown> = {}; // Add index signature to result object

    Object.keys(obj ?? {}).forEach((key) => {
        if (typeof obj?.[key] !== 'undefined' && obj[key] !== null) {
            if (deep && isPlainObject(obj[key] as Record<string, unknown>)) {
                result[key] = trimObject(obj?.[key] as Record<string, unknown>, true);
            } else {
                result[key] = obj[key];
            }
        }
    });

    return result;
}

export function isString(str: unknown): boolean {
    return typeof str === 'string';
}

export function isFunction(fn?: null | (() => unknown) | unknown): boolean {
    return {}.toString.call(fn) === `[object Function]`;
}

export function ensureHttps(url: string): string | null {
    try {
        const prependedUrl: string = !url.match(/^[a-zA-Z]+:/) ? `https://${url}` : url;
        const parsed: URL = new URL(prependedUrl);
        if (parsed.hostname === 'localhost' || parsed.protocol === 'https:') {
            return prependedUrl;
        }

        return url.replace(/^http:/i, 'https:');
    } catch (e) {
        return null;
    }
}

export function getDomain(hostname: string | null = window?.location?.hostname): string {
    let domain: string = hostname ?? '';

    if (typeof hostname !== 'undefined' && hostname !== null) {
        const parts: string[] = hostname.split('.').reverse();

        if (parts !== null && parts.length > 1) {
            domain = `${parts[1]}.${parts[0]}`;

            if (['co.uk', 'co.in'].includes(domain) && parts.length > 2) {
                domain = `${parts[2]}.${domain}`;
            }
        }
    }

    return domain;
}

export function getAllowedDomains(hostname: string = window.location.hostname): string[] {
    const parsed: UriResult = parseUri(hostname);
    const replacedHostname: string = getDomain(parsed.hostname);

    const domains: string[] = [ensureHttps(replacedHostname) ?? '', ensureHttps(hostname) ?? ''];
    const allowedDomains: string[] = [...new Set(domains)];

    return allowedDomains;
}

export function voidFn(): void {}
